'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users, Car, Package, Building2, TrendingUp, RefreshCw } from 'lucide-react';

interface TechnicianStat {
  name: string;
  jobs: number;
  parts: number;
}

interface VehicleFailure {
  make: string;
  count: number;
}

interface PartConsumption {
  partNumber: string;
  name: string;
  total: number;
}

interface BranchMetric {
  branch: string;
  jobs: number;
  vehicles: number;
}

const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d'];

export default function ReportsClient() {
  const [techStats, setTechStats] = useState<TechnicianStat[]>([]);
  const [vehicleFailures, setVehicleFailures] = useState<VehicleFailure[]>([]);
  const [partConsumption, setPartConsumption] = useState<PartConsumption[]>([]);
  const [branchMetrics, setBranchMetrics] = useState<BranchMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchReports = async () => {
    setLoading(true);
    const supabase = createClient();

    // Fetch all job cards with their parts
    const { data: cards } = await supabase
      .from('job_cards')
      .select('id, technician, branch, make, vehicle_reg, submitted_at');

    const { data: parts } = await supabase
      .from('job_card_parts')
      .select('job_card_id, part_number, part_name, qty');

    const cardList = cards || [];
    const partList = parts || [];

    // ── Technician stats ─────────────────────────────────────────────────────
    const techMap: Record<string, { jobs: number; parts: number }> = {};
    cardList.forEach((c) => {
      const t = c.technician as string;
      if (!techMap[t]) techMap[t] = { jobs: 0, parts: 0 };
      techMap[t].jobs += 1;
    });
    partList.forEach((p) => {
      const card = cardList.find((c) => c.id === p.job_card_id);
      if (card) {
        const t = card.technician as string;
        if (techMap[t]) techMap[t].parts += (p.qty as number) || 0;
      }
    });
    const techArr: TechnicianStat[] = Object.entries(techMap)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.jobs - a.jobs)
      .slice(0, 10);
    setTechStats(techArr);

    // ── Vehicle failure patterns (by make) ───────────────────────────────────
    const makeMap: Record<string, number> = {};
    cardList.forEach((c) => {
      const make = (c.make as string) || 'Unknown';
      makeMap[make] = (makeMap[make] || 0) + 1;
    });
    const vehicleArr: VehicleFailure[] = Object.entries(makeMap)
      .map(([make, count]) => ({ make, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
    setVehicleFailures(vehicleArr);

    // ── Parts consumption ────────────────────────────────────────────────────
    const partMap: Record<string, { name: string; total: number }> = {};
    partList.forEach((p) => {
      const pn = p.part_number as string;
      if (!partMap[pn]) partMap[pn] = { name: p.part_name as string, total: 0 };
      partMap[pn].total += (p.qty as number) || 0;
    });
    const partArr: PartConsumption[] = Object.entries(partMap)
      .map(([partNumber, v]) => ({ partNumber, ...v }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
    setPartConsumption(partArr);

    // ── Branch performance ───────────────────────────────────────────────────
    const branchMap: Record<string, { jobs: number; vehicles: Set<string> }> = {};
    cardList.forEach((c) => {
      const br = c.branch as string;
      if (!branchMap[br]) branchMap[br] = { jobs: 0, vehicles: new Set() };
      branchMap[br].jobs += 1;
      if (c.vehicle_reg) branchMap[br].vehicles.add((c.vehicle_reg as string).toUpperCase());
    });
    const branchArr: BranchMetric[] = Object.entries(branchMap)
      .map(([branch, v]) => ({ branch, jobs: v.jobs, vehicles: v.vehicles.size }))
      .sort((a, b) => b.jobs - a.jobs);
    setBranchMetrics(branchArr);

    setLastRefresh(new Date());
    setLoading(false);
  };

  useEffect(() => {
    fetchReports();

    // Real-time refresh when job cards change
    const supabase = createClient();
    const channel = supabase
      .channel('reports_refresh')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_cards' }, () => {
        fetchReports();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_card_parts' }, () => {
        fetchReports();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const totalJobs = techStats.reduce((s, t) => s + t.jobs, 0);
  const totalParts = partConsumption.reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Business Reports</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Job trends, vehicle patterns, parts consumption &amp; branch performance
            {lastRefresh && (
              <span className="ml-2 text-xs text-muted-foreground">
                · Updated {lastRefresh.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={fetchReports}
          disabled={loading}
          className="btn-secondary flex items-center gap-2 self-start sm:self-auto min-h-[44px]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {[
          { label: 'Total Job Cards', value: totalJobs, icon: TrendingUp, color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Technicians Active', value: techStats.length, icon: Users, color: 'text-positive', bg: 'bg-positive/10' },
          { label: 'Parts Consumed', value: totalParts, icon: Package, color: 'text-warning', bg: 'bg-warning/10' },
          { label: 'Branches Reporting', value: branchMetrics.length, icon: Building2, color: 'text-accent', bg: 'bg-accent/10' },
        ].map((kpi) => (
          <div key={kpi.label} className="card-surface p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${kpi.bg}`}>
              <kpi.icon size={18} className={kpi.color} />
            </div>
            <div>
              <p className="font-mono-data text-xl font-bold text-foreground">
                {loading ? '…' : kpi.value}
              </p>
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Row 1: Technician jobs + Vehicle failures */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Technician job trends */}
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Jobs by Technician</h2>
              <p className="text-xs text-muted-foreground">Total job cards submitted per technician</p>
            </div>
          </div>
          {loading ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : techStats.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No data yet — submit job cards to see trends</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={techStats} margin={{ top: 4, right: 8, left: -20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: 'var(--foreground)' }}
                />
                <Bar dataKey="jobs" name="Job Cards" fill="#2563eb" radius={[3, 3, 0, 0]} />
                <Bar dataKey="parts" name="Parts Used" fill="#16a34a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Vehicle failure patterns */}
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-danger/10 flex items-center justify-center">
              <Car size={16} className="text-danger" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Vehicle Failure Patterns</h2>
              <p className="text-xs text-muted-foreground">Job cards by vehicle make — identifies high-failure models</p>
            </div>
          </div>
          {loading ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : vehicleFailures.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No vehicle data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={vehicleFailures}
                  dataKey="count"
                  nameKey="make"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ make, percent }) => `${make} ${((percent || 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {vehicleFailures.map((_, idx) => (
                    <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                />
                <Legend
                  formatter={(value) => <span style={{ color: 'var(--muted-foreground)', fontSize: '11px' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Row 2: Parts consumption + Branch performance */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Parts consumption trends */}
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
              <Package size={16} className="text-warning" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Parts Consumption</h2>
              <p className="text-xs text-muted-foreground">Top 10 most-used parts across all job cards</p>
            </div>
          </div>
          {loading ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : partConsumption.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No parts data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={partConsumption} layout="vertical" margin={{ top: 4, right: 20, left: 60, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'var(--muted-foreground)', fontSize: 10 }} allowDecimals={false} />
                <YAxis
                  type="category"
                  dataKey="partNumber"
                  tick={{ fill: 'var(--muted-foreground)', fontSize: 9 }}
                  width={55}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value, name, props) => [value, props?.payload?.name || 'Qty']}
                />
                <Bar dataKey="total" name="Qty Used" fill="#d97706" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Branch performance */}
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
              <Building2 size={16} className="text-accent" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">Branch Performance</h2>
              <p className="text-xs text-muted-foreground">Job cards and unique vehicles per branch</p>
            </div>
          </div>
          {loading ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : branchMetrics.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No branch data yet</div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    {['Branch', 'Job Cards', 'Vehicles', 'Avg Parts/Job'].map((col) => (
                      <th key={col} className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap" style={{ color: 'var(--muted-foreground)' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {branchMetrics.map((b, idx) => {
                    const techForBranch = techStats.filter(() => true);
                    const totalPartsForBranch = partConsumption.reduce((s, p) => s + p.total, 0);
                    const avgParts = b.jobs > 0 ? (totalPartsForBranch / totalJobs).toFixed(1) : '0';
                    return (
                      <tr key={b.branch} className="hover:bg-secondary/40 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                            />
                            <span className="text-xs font-medium text-foreground">{b.branch}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-mono-data text-xs font-bold text-primary">{b.jobs}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-mono-data text-xs text-foreground">{b.vehicles}</span>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="font-mono-data text-xs text-muted-foreground">{avgParts}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
